/* eslint-disable no-undef */
/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React from 'react';
import { connect } from 'react-redux';
import xor from 'lodash/xor';
// @ts-ignore
import MarkerClusterer from '@google/markerclustererplus';
import { RootState } from '../../../store';
import { HubsMarkerIcon, defaultHubsSvgProps, HubsSvgProps } from './HubsMarkerIcon';
import { requestHubs } from '../../../store/hubs/actions';
import Hub from '../../../shared/entities/hub';
import { ThemeInterface } from '../../common/theme/AppThemeInterface';
import appTheme from '../../common/theme/appTheme.json';
import HubNamePanel from './HubNamePanel';
import ClusterNamesPanel from './ClusterNamesPanel';
import { getNetworkGroupColorByBeamId } from '../../../utils/GlobalUtils';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../common/map/GoogleMapSingleton';

const theme: ThemeInterface = appTheme;
const MARKER_CUSTOM_FIELD_NAME_HUB_DETAILS = 'hubDetails';

interface HubsContainerProps {
  map: google.maps.Map;
}

interface HubsContainerState {
  hoveredMarker?: Cluster | google.maps.Marker;
}

interface HubMarker {
  marker: google.maps.Marker;
  count: number;
}

export interface HubDetails extends Hub {
  hubIconProps: HubsSvgProps;
}

class HubsContainer extends React.PureComponent<HubsContainerProps & StateProps & DispatchProps, HubsContainerState> {
  private hubToMarkerMap: { [hubId: string]: HubMarker };

  private markerClusterer: MarkerClusterer;

  private overlayView: google.maps.OverlayView;

  public constructor(props: HubsContainerProps & StateProps & DispatchProps) {
    super(props);

    this.hubToMarkerMap = {};
    this.markerClusterer = null;

    this.overlayView = new google.maps.OverlayView();
    this.overlayView.setMap(props.map);

    this.state = {
      hoveredMarker: undefined,
    };
  }

  /**
   * Request the hubs data from backend at component mount
   */
  public componentDidMount(): void {
    const { requestHubs } = this.props;
    requestHubs();
  }

  public componentDidUpdate(prevProps: StateProps & DispatchProps): void {
    const { hubsOnMap } = this.props;

    // Get a list of hubId that have been either added or removed to/from the map
    const changedHubsOnMap: string[] = xor(hubsOnMap, prevProps.hubsOnMap);

    // Update only the changed markers
    if (changedHubsOnMap && changedHubsOnMap.length) {
      this.updateMarkersOnMap(changedHubsOnMap);
    }
  }

  /**
   * Adds or removes the hub markers from the map based on the beams that have either been selected or deselected from showing on the map.
   * This function also updates the marker clusters.
   */
  private updateMarkersOnMap = (changedHubsOnMap: string[]): void => {
    const { hubsMap, hubsOnMap } = this.props;

    if (!hubsMap || !hubsOnMap) {
      return;
    }

    changedHubsOnMap.forEach((hubId: string) => {
      if (hubsOnMap.includes(hubId)) {
        // Beam was added to the map
        this.addHubToMap(hubsMap[hubId]);
      } else {
        // Beam was removed from the map
        this.removeHubFromMap(hubsMap[hubId]);
      }
    });

    // Render clusters all at ones to avoid flashing
    this.markerClusterer.repaint();
  };

  /**
   * Adds a single hub to the map
   */
  private addHubToMap = (hub: Hub): void => {
    if (hub) {
      // Get the hub marker if it already exists on the map
      let hubMarker = this.hubToMarkerMap[hub.id];

      // Create a new hub marker only if not already on the map unless it is a Ka (Viasat) network hub
      if (!hubMarker) {
        hubMarker = {
          marker: this.createHubMarker(hub), // ASSUMPTION: a hub can only have beams of the same network
          count: 1,
        };
        this.hubToMarkerMap[hub.id] = hubMarker;

        // Add the new marker to the clusterer
        if (this.markerClusterer === null) {
          // Create clusterer for the first time
          this.createHubMarkerClusterer([hubMarker.marker]);
        } else {
          // The clusterer already exists
          // Do not draw clusters to avoid flashing. They will be drawn using this.markerClusterer.repaint()
          this.markerClusterer.addMarker(hubMarker.marker, true);
        }
      } else {
        // Increase count for hub marker that already exists on the map
        // ASSUMPTION: a hub can only have beams of the same network
        hubMarker.count += 1;
      }
    }
  };

  /**
   * Removes a single hub from the map
   */
  private removeHubFromMap = (hub: Hub): void => {
    if (hub) {
      // Get marker from local map
      const hubMarker = this.hubToMarkerMap[hub.id];

      // Remove hub marker from the map or reduce its count
      if (hubMarker) {
        if (hubMarker.count === 1) {
          // Remove markers from clusterer
          // Do not draw clusters to avoid flashing. They will be drawn using this.markerClusterer.repaint()
          this.markerClusterer.removeMarker(hubMarker.marker, true);

          // Remove marker from map and local map
          hubMarker.marker.setMap(null);
          delete this.hubToMarkerMap[hub.id];
        } else {
          hubMarker.count -= 1;
        }
      }
    }
  };

  /**
   * Creates a Google Maps Marker object that includes the SVG to represent the hub marker
   */
  private createHubMarker = (hub: Hub): google.maps.Marker => {
    const { map, specialBeamsNetworkMapping } = this.props;

    // Get hub color from one of the hub beams (ASSUMPTION: a hub can only have beams of the same network)
    let hubColor = theme.hubs.innerDiamondFill;
    if (hub.beams.length) {
      hubColor = getNetworkGroupColorByBeamId(specialBeamsNetworkMapping, hub.beams[0]);
    }

    const iconProps = {
      ...defaultHubsSvgProps,
      innerDiamond: {
        ...defaultHubsSvgProps.innerDiamond,
        fill: hubColor,
      },
    };
    const icon = HubsMarkerIcon(iconProps);
    const iconUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(icon)}`;

    const marker = new google.maps.Marker({
      position: { lat: hub.latitudeDeg, lng: hub.longitudeDeg },
      map,
      icon: {
        url: iconUrl,
        anchor: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(32, 32), // needed for the marker to be visible in IE 11
      },
    });

    // Add the hubDetails to a marker custom field for easy access when used to open marker name panel
    const hubDetails: HubDetails = {
      ...hub,
      hubIconProps: iconProps,
    };
    marker.set(MARKER_CUSTOM_FIELD_NAME_HUB_DETAILS, hubDetails);

    // Add mouseover listener to set the current hovered marker in the component state
    marker.addListener('mouseover', () => {
      this.setState({
        hoveredMarker: marker,
      });
    });

    return marker;
  };

  /**
   * Returns the Marker Icon Props for a cluster marker
   */
  private getClusterMarkerIconProps = (): HubsSvgProps => {
    return { ...defaultHubsSvgProps };
  };

  /**
   * Creates a MarkerClusterer object that will cluster hub markers on the map based on the zoom level.
   * The MarkerClusterer will use the icon defined in the SVG returned by HubsMarkerIcon call.
   */
  private createHubMarkerClusterer = (addedBeamsMarker: google.maps.Marker[]): void => {
    const { map } = this.props;

    const iconProps = this.getClusterMarkerIconProps();
    const icon = HubsMarkerIcon(iconProps);
    const iconUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(icon)}`;

    this.markerClusterer = new MarkerClusterer(map, addedBeamsMarker, {
      styles: [
        {
          url: iconUrl,
          anchorIcon: [0, 0],
          width: theme.hubs.iconWidth,
          height: theme.hubs.iconHeight,
          textColor: 'transparent', // set textColor to theme.hubs.diamondSecondaryFill if cluster number needs to be shown
        },
      ],
      zoomOnClick: false,
    });

    // Add mouseover listener to set the current hovered marker in the component state
    this.markerClusterer.addListener('mouseover', (cluster: Cluster) => {
      this.setState({
        hoveredMarker: cluster,
      });
    });
  };

  /**
   * Reset the hovered marker to close the previously hovered panel
   */
  private mouseOutOnPanelHandler = (): void => {
    this.setState({
      hoveredMarker: undefined,
    });
  };

  public render(): React.ReactComponentElement<'div', {}> | null {
    const { hoveredMarker } = this.state;

    // Render a single hub name panel
    if (hoveredMarker && 'get' in hoveredMarker) {
      const hubDetails: HubDetails = hoveredMarker.get(MARKER_CUSTOM_FIELD_NAME_HUB_DETAILS);

      const point = this.overlayView
        .getProjection()
        .fromLatLngToContainerPixel(new google.maps.LatLng(hubDetails.latitudeDeg, hubDetails.longitudeDeg));

      return (
        <HubNamePanel
          hubName={hubDetails.name}
          iconProps={hubDetails.hubIconProps}
          left={Math.abs(point.x)}
          top={Math.abs(point.y)}
          mouseLeaveHandler={this.mouseOutOnPanelHandler}
        />
      );
    }

    // Render a cluster names panel
    if (hoveredMarker && !('get' in hoveredMarker)) {
      const point = this.overlayView
        .getProjection()
        .fromLatLngToContainerPixel(
          new google.maps.LatLng(hoveredMarker.getCenter().lat(), hoveredMarker.getCenter().lng()),
        );

      const markers: google.maps.Marker[] = hoveredMarker.getMarkers();

      if (markers && markers.length) {
        const hubDetailsArray: HubDetails[] = markers.map((marker: google.maps.Marker) =>
          marker.get(MARKER_CUSTOM_FIELD_NAME_HUB_DETAILS),
        );

        return (
          <ClusterNamesPanel
            hubDetailsArray={hubDetailsArray}
            iconProps={this.getClusterMarkerIconProps()}
            left={Math.abs(point.x)}
            top={Math.abs(point.y)}
            mouseLeaveHandler={this.mouseOutOnPanelHandler}
          />
        );
      }
    }

    // Hide panel if no marker is hovered
    return null;
  }
}

const mapStateToProps = (state: RootState, ownProps: HubsContainerProps): any => ({
  specialBeamsNetworkMapping: state.config.appConfig.specialBeamsNetworkMapping,
  hubsMap: state.hubs.hubsMap,
  hubsOnMap:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]?.hubsOnMap,

  // TODO: the following will be needed in future implementations to hide the next beam hub by default and show it only when the mouse hovers the next beam
  // nextBeam: state.ui.overlaysUiState[state.ui.overlaysCurrentContext]?.nextBeam,
  // hoveredBeamId: state.ui.hoveredBeamId,
});
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = {
  requestHubs,
};
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(HubsContainer);
